import React, { useEffect } from 'react';
import { RxCross1 } from 'react-icons/rx';
import { HiLocationMarker } from 'react-icons/hi';
import { MdDirectionsCar } from 'react-icons/md';
import { IoSparkles } from 'react-icons/io5';
import { formatDate, formatTimeInTimezone } from 'pages/utils/formdatedynamics';
import './PopupInvoice.css';

function PackageInvoicePopup({ isOpen, onClose, data }) {

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (e.target.classList.contains('pi-overlay')) onClose();
    };
    if (isOpen) document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isOpen, onClose]);

  if (!data) return null;

  const toNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const packagePrice = toNumber(data.pricePaid ?? data.price);
  const platformFee = toNumber(data.platformFeePaid ?? data.platformFee);
  const total = toNumber(data.total) || packagePrice;

  const formatOrderId = (item) => {
    if (!item?._id) return '';
    const orderId = `${item._id.slice(0, 4)}${item._id.slice(-4)}`.toUpperCase();
    return `WS-PKG-${orderId}`;
  };

  const vehicleLabel = data.carType
    ? `${data.carMake || ''} ${data.carModel || ''} · ${data.carType}`.trim()
    : data.plateNumber || '—';

  const cap = (str) => (str ? str.charAt(0).toUpperCase() + str.slice(1) : '—');

  const paidDate = data.paidAt
    ? `${formatDate(data.paidAt)} · ${formatTimeInTimezone(data.paidAt)}`
    : '—';

  return (
    <div className={`pi-overlay ${isOpen ? 'pi-open' : 'pi-close'}`}>
      <div className={`pi-modal ${isOpen ? 'pi-slide-in' : 'pi-slide-out'}`}>

        <div className="pi-header">
          <div className="pi-header-top">
            <div>
              <p className="pi-label-sm">Package invoice</p>
              <p className="pi-order-id">{formatOrderId(data)}</p>
            </div>
            <div className="pi-header-actions">
              <span className={`pi-badge pi-status-${data.status}`}>{cap(data.status)}</span>
              <RxCross1 className="pi-close-icon" onClick={onClose} />
            </div>
          </div>
          <div className="pi-header-meta">
            <div>
              <p className="pi-meta-label">Date</p>
              <p className="pi-meta-value">{paidDate}</p>
            </div>
            <div>
              <p className="pi-meta-label">Billing</p>
              <p className="pi-meta-value">{cap(data.billingStatus)}</p>
            </div>
          </div>
        </div>

        <div className="pi-body">

          <div className="pi-info-grid">
            <div className="pi-info-card">
              <p className="pi-info-label">
                <HiLocationMarker className="pi-info-icon" />
                Location
              </p>
              <p className="pi-info-value">{data.locationDisplayName || data.location?.displayName || '—'}</p>
            </div>
            <div className="pi-info-card">
              <p className="pi-info-label">
                <MdDirectionsCar className="pi-info-icon" />
                Vehicle
              </p>
              <p className="pi-info-value">{vehicleLabel}</p>
            </div>
          </div>

          <div className="pi-service-card">
            <p className="pi-info-label">
              <IoSparkles className="pi-info-icon" />
              Package
            </p>
            <div className="pi-service-row">
              <p className="pi-info-value">
                {data.washCount ? `${data.washCount} washes / month` : data.tierName || '—'}
              </p>
              {data.daysPerWeek && (
                <span className="pi-variant-badge">{data.daysPerWeek}x / week</span>
              )}
            </div>
          </div>

          <div className="pi-breakdown">
            <p className="pi-breakdown-title">Price breakdown</p>

            <div className="pi-line-item">
              <span className="pi-line-label">Package price</span>
              <span className="pi-line-value">{packagePrice.toFixed(2)} AED</span>
            </div>

            <div className="pi-line-item">
              <span className="pi-line-label pi-fee-label">Platform fee</span>
              <span className="pi-line-value pi-fee-value">− {platformFee.toFixed(2)} AED</span>
            </div>

            <div className="pi-dashed-divider" />

            <div className="pi-total-row">
              <span className="pi-total-label">Customer charged</span>
              <span className="pi-total-value">{total.toFixed(2)} AED</span>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default PackageInvoicePopup;
